import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Formik, FormikConsumer } from 'formik';
import { Form } from 'formik-antd';
import { Col, Row, Button, Card, theme, Checkbox } from 'antd';
import { SettingOutlined } from '@ant-design/icons';
import InputField from 'components/Forms/input';
import CheckboxField from 'components/Forms/checkbox';
import useStyles from 'components/Forms/styles';
import GeolocationField from 'components/Forms/geolocation';
import NotificationTemplatesModal from 'components/NotificationTemplates';
import {
  useNotificationConfig,
  useSaveNotificationConfig,
} from 'services/NotificationTemplatesService';
import { MARKETPLACE_OWNERTYPE } from 'constants/defaultValues';

const MarketplaceNotifications = ({ ownerId, ownerName }) => {
  const [channelOpen, setChannelOpen] = useState(null);
  const numericOwnerId = ownerId ? Number(ownerId) : undefined;
  const { data: config, isLoading } = useNotificationConfig(
    MARKETPLACE_OWNERTYPE,
    numericOwnerId,
  );
  const saveConfig = useSaveNotificationConfig();

  if (!ownerId) return null;

  const mailStates = config?.email?.states ?? [];
  const mailActive = mailStates.some((s) => s.enabled);
  const hasConfig = !!config;

  const handleToggle = (e) => {
    if (!config) return;
    const next = e.target.checked;
    saveConfig.mutate({
      ...config,
      email: {
        ...config.email,
        states: config.email.states.map((s) => ({
          ...s,
          enabled: next ? Boolean(s.templateId) : false,
        })),
      },
    });
  };

  return (
    <div style={{ marginLeft: 16 }}>
      <Checkbox
        checked={mailActive}
        disabled={!hasConfig || isLoading || saveConfig.isPending}
        onChange={handleToggle}
        style={{ width: '100%' }}
      >
        Envío de notificaciones a usuarios
      </Checkbox>
      <Row style={{ marginTop: 12 }}>
        <Button
          type="link"
          size="small"
          icon={<SettingOutlined />}
          style={{ paddingLeft: 0 }}
          onClick={() => setChannelOpen('email')}
        >
          Configurar plantillas de mail
        </Button>
        <Button
          type="link"
          size="small"
          icon={<SettingOutlined />}
          onClick={() => setChannelOpen('whatsapp')}
        >
          Configurar plantillas de WhatsApp
        </Button>
      </Row>
      <NotificationTemplatesModal
        open={channelOpen !== null}
        channel={channelOpen || undefined}
        onClose={() => setChannelOpen(null)}
        ownerType={MARKETPLACE_OWNERTYPE}
        ownerId={numericOwnerId}
        ownerName={ownerName}
      />
    </div>
  );
};

MarketplaceNotifications.propTypes = {
  ownerId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  ownerName: PropTypes.string,
};

MarketplaceNotifications.defaultProps = {
  ownerId: null,
  ownerName: '',
};

const Marketplace = ({
  onSubmit,
  initialValues,
  validationSchema,
  loading,
  ownerId,
  ownerName,
}) => {
  const { token } = theme.useToken();
  const classes = useStyles({ theme: token });
  return (
    <Formik
      enableReinitialize
      initialValues={initialValues}
      onSubmit={onSubmit}
      validationSchema={validationSchema}
    >
      <Form>
        <Card
          title="Información del Marketplace"
          headStyle={{
            borderBottom: `3px solid ${token.colorPrimary}`,
          }}
        >
          <Row>
            <Col span={24} className={classes.cardContent}>
              <Row gutter={[8, 16]}>
                <Col xs={12}>
                  <InputField label="Nombre" fieldName="name" />
                </Col>
                <Col xs={12}>
                  <InputField label="Detalle" fieldName="detail" />
                </Col>
                <Col xs={8}>
                  <InputField
                    label="Contacto comercial"
                    fieldName="commercialContact"
                  />
                </Col>
                <Col xs={8}>
                  <InputField
                    label="Teléfono de contacto comercial"
                    fieldName="commercialPhone"
                  />
                </Col>
                <Col xs={8}>
                  <InputField
                    label="Email de contacto comercial"
                    fieldName="commercialEmail"
                  />
                </Col>
                <Col xs={8}>
                  <InputField
                    label="Contacto soporte"
                    fieldName="contactSupport"
                  />
                </Col>
                <Col xs={8}>
                  <InputField
                    label="Teléfono contacto soporte"
                    fieldName="phoneSupport"
                  />
                </Col>
                <Col xs={8}>
                  <InputField
                    label="Email contacto soporte"
                    fieldName="emailSupport"
                  />
                </Col>
                <Col xs={12}>
                  <Row>
                    <Col xs={24}>
                      <InputField
                        type="string"
                        label="Dirección de despacho"
                        fieldName="dispatchAddress"
                      />
                    </Col>

                    <Col xs={24}>
                      <GeolocationField addressFieldName="dispatchAddress" />
                    </Col>
                  </Row>
                </Col>
                <Col xs={12}>
                  <Row>
                    <InputField
                      containerStyle={{ width: '100%' }}
                      label="Representante comercial PaQuery"
                      fieldName="commercialAgent"
                    />
                  </Row>
                  <Row gutter={32}>
                    <Col style={{ marginLeft: 16 }}>
                      <CheckboxField label="Publicar" fieldName="published" />
                      <CheckboxField
                        label="Paquetes del tipo Pickup con stock"
                        fieldName="stock"
                      />
                    </Col>
                  </Row>
                  <MarketplaceNotifications
                    ownerId={ownerId}
                    ownerName={ownerName}
                  />
                </Col>
              </Row>
            </Col>
          </Row>
        </Card>
        <Row>
          <Col span={24} style={{ justifyContent: 'end', display: 'flex' }}>
            <FormikConsumer>
              {({ status, isValid }) => (
                <Button
                  type="primary"
                  htmlType="submit"
                  className={classes.saveButton}
                  style={{ marginBottom: 5 }}
                  size="large"
                  loading={loading || status?.geolocating}
                  disabled={!isValid}
                >
                  Guardar
                </Button>
              )}
            </FormikConsumer>
          </Col>
        </Row>
      </Form>
    </Formik>
  );
};

Marketplace.propTypes = {
  initialValues: PropTypes.shape({
    name: PropTypes.string,
    detail: PropTypes.string,
    publish: PropTypes.bool,
    userNotifications: PropTypes.bool,
    pickupStock: PropTypes.bool,
  }).isRequired,
  onSubmit: PropTypes.func.isRequired,
  validationSchema: PropTypes.shape({}).isRequired,
  loading: PropTypes.bool.isRequired,
  ownerId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  ownerName: PropTypes.string,
};

Marketplace.defaultProps = {
  ownerId: null,
  ownerName: '',
};

export default Marketplace;
